import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';
import type { PropsWithChildren } from 'react';

import React, { createContext, useContext, useMemo, useState } from 'react';

import DeleteBranchModal from '.';

interface DeleteBranchModalContextValue {
  /**
   * Indicates whether a branch is preparing to be deleted or not.
   */
  branchToDelete: BranchRepresentationType | null;

  /**
   * Configures the delete branch modal by assigning it a `branch`. Setting
   * `branch` to `null` closes the modal.
   * @example
   * setBranchToDelete(branch)
   * setBranchToDelete(null)
   */
  setBranchToDelete: React.Dispatch<React.SetStateAction<BranchRepresentationType | null>>;
}

const DeleteBranchModalContext = createContext<DeleteBranchModalContextValue | null>(null);
DeleteBranchModalContext.displayName = 'DeleteBranchModalContext';

export const DeleteBranchModalProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [branchToDelete, setBranchToDelete] = useState<DeleteBranchModalContextValue['branchToDelete']>(null);

  return (
    <DeleteBranchModalContext.Provider
      value={useMemo(
        () => ({
          branchToDelete,
          setBranchToDelete,
        }),
        [branchToDelete],
      )}
    >
      {children}
      <DeleteBranchModal />
    </DeleteBranchModalContext.Provider>
  );
};

/**
 * Returns state that controls whether `<DeleteBranchModal>` is open.
 */
export const useDeleteBranchModalContext = () => {
  const value = useContext(DeleteBranchModalContext);
  if (!value) throw new Error('DeleteBranchModalProvider is missing');
  return value;
};
